class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.minSearchChars = 2;
    this.resultsLimit = Number.parseInt(this.dataset.resultsLimit, 10) || 5;
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.overlay = document.querySelector(".header__overlay");
    this.body = document.querySelector("body");
    this.headerTop = document.querySelector(".header__top");
    this.headerBottom = document.querySelector(".header__bottom");
    this.promoModal = document.querySelector(".search__modal");
    this.headerInput = document.querySelector(".header__search .search__input");
    this.setupEventListeners();
  }

  detectAdultContext() {
    try {
      if (document.body.classList.contains('adult-context')) return true;
      return sessionStorage.getItem('eupoupo_age_verified') === 'true'
        && sessionStorage.getItem('eupoupo_adult_search') === 'true';
    } catch (e) {
      return document.body.classList.contains('adult-context');
    }
  }

  buildQueryKey(searchTerm, isAdult) {
    return searchTerm.trim().replace(/\s+/g, "-").toLowerCase() + (isAdult ? "|a" : "|s");
  }

  normalizePredictiveQuery(searchTerm, isAdult) {
    if (typeof window.eupoupoNormalizeSearchQuery === "function") {
      return window.eupoupoNormalizeSearchQuery(searchTerm, isAdult);
    }
    const rawValue = (searchTerm || "").trim();
    if (!rawValue) return rawValue;
    const withoutAdultTag = rawValue.replace(/\s*-tag:\+18/g, "").trim();
    return isAdult ? withoutAdultTag : `${withoutAdultTag} -tag:+18`.trim();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      if (this.promoModal && this.classList.contains("search-modal__form")) {
        this.promoModal.classList.remove("search__modal--hidden");
      }
      return;
    }

    if (this.searchTerm.length < this.minSearchChars) {
      // Close dropdown only — do not clear input (close(true) wiped 1-char queries on slow typing)
      this.close(false);
      if (this.promoModal && this.classList.contains("search-modal__form")) {
        this.promoModal.classList.remove("search__modal--hidden");
      }
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (this.classList.contains("search-modal__form")) {
      this.overlay.classList.add("visible");
      this.headerTop.classList.add("header__top--search");
      //this.body.classList.add("overflow-hidden");
      if (this.headerBottom) {
        this.headerBottom.classList.add("header__bottom--visible");
      }
      if (this.promoModal) {
        this.promoModal.classList.remove("search__modal--hidden");
      }
    }

    if (!currentSearchTerm.length) {
      return;
    }

    if (currentSearchTerm.length < this.minSearchChars) {
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      if (this.promoModal) {
        this.promoModal.classList.add("search__modal--hidden");
      }
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    if (this.classList.contains("search-modal__form")) {
      this.headerTop.classList.remove("header__top--search");
      this.overlay.classList.remove("visible");
      //this.body.classList.remove("overflow-hidden");
      if (this.headerBottom) {
        this.headerBottom.classList.remove("header__bottom--visible");
      }
    }
    if (
      event?.relatedTarget &&
      this.contains(event.relatedTarget)
    ) {
      return;
    }
    if (event?.relatedTarget && !this.contains(event.relatedTarget)) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
      return;
    }
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onBlur() {
    this.headerInput.blur();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
      case "Escape":
        event.preventDefault();
        this.closeResults(true);
        if (this.promoModal && this.classList.contains("search-modal__form")) {
          this.promoModal.classList.add("search__modal--hidden");
        }
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const previousTermMatches = currentButtonText.match(new RegExp(escapedPreviousTerm, "g"));
      if (previousTermMatches && previousTermMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const allElements = Array.from(this.querySelectorAll("li[role='option']"));
    if (!allElements.length) return;

    const selectedElement = this.querySelector('[aria-selected="true"]');
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;

    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement =
        selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    allElements.forEach((li) => {
      li.setAttribute("aria-selected", "false");
      const link = li.querySelector("a, button");
      if (link) link.setAttribute("tabindex", "-1");
    });

    activeElement.setAttribute("aria-selected", "true");
    const activeLink = activeElement.querySelector("a, button");
    if (activeLink) {
      activeLink.setAttribute("tabindex", "0");
      if (typeof activeLink.focus === "function") {
        try {
          activeLink.focus({ preventScroll: true });
        } catch (e) {
          activeLink.focus();
        }
      }
      activeElement.scrollIntoView({ block: "nearest", inline: "nearest" });
    }

    if (this.statusElement) {
      this.setLiveRegionText(activeElement.textContent.trim());
    }
    this.input.setAttribute(
      "aria-activedescendant",
      activeElement.id || ""
    );
  }

  async getSearchResults(searchTerm) {
    const isAdult = this.detectAdultContext();
    const normalizedTerm = this.normalizePredictiveQuery(searchTerm, isAdult);
    const queryKey = this.buildQueryKey(searchTerm, isAdult);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      const cachedMarkup = this.cachedResults[queryKey];
      requestAnimationFrame(() => this.renderSearchResults(cachedMarkup));
      return;
    }

    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }

    try {
      // Pass adult=1 only when explicit adult search is enabled (or on adult collection pages)
      const adultParam = isAdult ? "&adult=1" : "";
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          normalizedTerm
        )}&resources[type]=product,article,collection&resources[limit]=${this.resultsLimit}&resources[limit_scope]=each&section_id=predictive-search${adultParam}`,
        { signal: this.abortController.signal }
      );
      if (!response.ok) {
        this.close();
        throw new Error(response.status);
      }
      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search").innerHTML;
      // Save bandwidth keeping the cache in all instances synced
      this.allPredictiveSearchInstances.forEach(
        (predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        }
      );
      requestAnimationFrame(() => this.renderSearchResults(resultsMarkup));
    } catch (error) {
      if (error?.name === 'AbortError') {
        // Code 20 means the call was aborted
        return;
      }
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.remove("search__modal--hidden");
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    //this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
    if (this.promoModal) {
      this.promoModal.classList.add("search__modal--hidden");
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
